import { Component, ElementRef, HostListener, signal, ChangeDetectionStrategy, inject, input, output } from '@angular/core';
import { SessionService } from '../../../services/session.service';
import { LayoutService } from '../../../../@vex/services/layout.service';
import { ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { Icons } from '../../../util/Icons';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatInput } from '@angular/material/input';

@Component({
	selector: 'ara-password-input',
	templateUrl: './ara-password-input.component.html',
	styleUrl: './ara-password-input.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		CdkOverlayOrigin,
		MatFormField,
		NgClass,
		ReactiveFormsModule,
		MatIconButton,
		MatIcon,
		NgTemplateOutlet,
		CdkConnectedOverlay,
		MatLabel,
		TranslocoDirective,
		MatInput,
		MatSuffix,
	],
})
export class AraPasswordInputComponent {
	sessionService = inject(SessionService);
	layoutService = inject(LayoutService);
	icons = inject(Icons);
	private elem = inject(ElementRef);

	readonly lowerCaseAmt = input<number>(1);
	readonly upperCaseAmt = input<number>(1);
	readonly digitAmt = input<number>(1);
	readonly specialCharAmt = input<number>(1);
	readonly minLength = input<number>(8);
	readonly showPasswordStrengthMeter = input<boolean>(false);
	readonly placePasswordStrengthMeterInInput = input<boolean>(false);
	readonly passwordChange = output<string>();
	readonly scoreUpdated = output<number>();
	readonly inputFocused = output<boolean>();

	passwordFC = new UntypedFormControl('', [Validators.required, Validators.minLength(1)]);
	lowerCaseRegex = '(?=(.*[a-z]){' + this.lowerCaseAmt() + ',})';
	upperCaseRegex = '(?=(.*[A-Z]){' + this.upperCaseAmt() + ',})';
	digitRegex = '(?=(.*[0-9]){' + this.digitAmt() + ',})';
	specialCharRegex = '(?=(.*[!@#$%^&*()\\-__+\'".]){' + this.specialCharAmt() + ',})';
	lengthRegex = '.{' + this.minLength() + ',}';
	completeRegex = '^' + this.lowerCaseRegex + this.upperCaseRegex + this.digitRegex + this.specialCharRegex + this.lengthRegex + '$';
	checkPasswordStrengthRegex = new RegExp(this.completeRegex);

	minLengthValid = signal(false);
	amtLowerCaseValid = signal(false);
	amtUpperCaseValid = signal(false);
	amtDigitValid = signal(false);
	amtSpecialCharValid = signal(false);
	isMobile = signal(false);

	isOpen = signal(false);
	showNewPassword = signal(false);
	score = signal(0);

	updatePassword() {
		this.passwordChange.emit(this.passwordFC.value);
	}

	emitFocusEvent(event) {
		this.inputFocused.emit(event);
	}

	checkLowerCase() {
		if (new RegExp(this.lowerCaseRegex).test(this.passwordFC.value)) {
			this.score.update((value) => value + 1);
			this.amtLowerCaseValid.set(true);
		}
	}

	checkUpperCase() {
		if (new RegExp(this.upperCaseRegex).test(this.passwordFC.value)) {
			this.score.update((value) => value + 1);
			this.amtUpperCaseValid.set(true);
		}
	}

	checkDigit() {
		if (new RegExp(this.digitRegex).test(this.passwordFC.value)) {
			this.score.update((value) => value + 1);
			this.amtDigitValid.set(true);
		}
	}

	checkSpecialChars() {
		if (new RegExp(this.specialCharRegex).test(this.passwordFC.value)) {
			this.score.update((value) => value + 1);
			this.amtSpecialCharValid.set(true);
		}
	}

	checkMinLength() {
		if (this.passwordFC.value.length >= this.minLength()) {
			this.score.update((value) => value + 1);
			this.minLengthValid.set(true);
		}
	}

	resetValidators() {
		this.minLengthValid.set(false);
		this.amtLowerCaseValid.set(false);
		this.amtUpperCaseValid.set(false);
		this.amtDigitValid.set(false);
		this.amtSpecialCharValid.set(false);
	}

	getPasswordStrength(): number {
		this.updatePassword();
		this.resetValidators();
		this.score.set(0);
		this.checkLowerCase();
		this.checkUpperCase();
		this.checkDigit();
		this.checkSpecialChars();
		this.checkMinLength();
		this.scoreUpdated.emit(this.score());
		return this.score();
	}

	@HostListener('document:click', ['$event'])
	DocumentClick(event: Event) {
		if (!this.elem.nativeElement.contains(event.target)) {
			// User clicked outside
			this.isOpen.set(false);
		}
	}
}
